import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { IAbsence, IUser } from '../../../../models/leads/leads.model';
import { debounce } from '../../../../helper/debounce';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import { IActionStudioSearchResult, IFrowardUsers } from "../../../../models/action.model";
import { LeadHelper } from '../../../../helper/lead.helper';
import {
  LeadsMultipleStepByStepService
} from '../../../../services/leads-multiple-step-by-step/leads-multiple-step-by-step.service';
import { ActionHelper } from '../../../../helper/action.helper';
import { Subscription } from 'rxjs';
import { TextHelper } from '../../../../helper/text.helper';

@Component({
  selector: 'fi-forward-request-popup',
  templateUrl: './forward-request-popup.component.html',
  styleUrls: ['./forward-request-popup.component.scss'],
  standalone: false,
})
export class ForwardRequestPopupComponent extends BaseCustomDialog implements OnDestroy {
  contactName = '';
  leadId: number;
  selectedUserId: number;
  forwardLeadTypeId: number;
  usersByArea: IActionStudioSearchResult[] = [];
  usersByHierarchy: IUser[] = [];
  studioUsersByHierarchy: IActionStudioSearchResult[] = [];
  selectActive = false;
  searchedText = '';
  isMany: boolean;
  leads: number[];
  searchTriggered = false;
  originalSearchedText = '';
  note = '';
  externalEmail = '';
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('LEAD_FORWARD', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('LEAD_FORWARD', true);
  submitting: boolean;
  tabTypes = { dropdown: 'dropdown', input: 'input' };
  selectedTab = this.tabTypes.dropdown;
  checkboxChecked = false;
  inValidNote = false;
  selectedPartner: IActionStudioSearchResult;
  selectedValue: any;
  displayedSelectedText: string = '';
  leadTypeRelationshipManagement = LeadHelper.LEAD_TYPE_PLANER_RELATIONSHIP
  userIsAbsent: boolean = false;
  isAbsentUntil: string;
  isAbsentCoverUserName: string;
  hasError: boolean = false;
  options: { shortName: string; name: string, id: number, byArea: boolean, value: IUser | IActionStudioSearchResult, hasKKZ: boolean }[] = [];
  sectionTextKeys = new Map<number, string>();
  unfilteredForwardedUsers: IFrowardUsers;
  step: number = 0;

  private lastSearch = '';
  private cancel = false;
  private subscription: Subscription;

  constructor(
    public dialogRef: MatDialogRef<ForwardRequestPopupComponent>,
    private leadsMultipleStepByStepService: LeadsMultipleStepByStepService,
    @Inject(MAT_DIALOG_DATA) public dialogData: {contactName: string, leadId: number, leadTypeId: number, title: string, externalEmail: string, leads: number[], isMany: boolean},
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    private dialog: MatDialog,
  ) {
    super(dialogRef);

    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.forwardLeadTypeId = dialogData?.leadTypeId;
    this.title = dialogData?.title;
    this.isMany = dialogData?.isMany;
    this.leads = dialogData?.leads;


    if (!this.isMany) {
      this.actionDataService.getForwardUsers(dialogData.leadId, dialogData.leadTypeId).subscribe(({ success, data }) => {
        if (success) {
          this.unfilteredForwardedUsers = data;
          this.usersByArea = data.ByArea ? data.ByArea : data.UsersByArea as any;
          this.usersByHierarchy = data.ByHierarchy ? data.ByHierarchy : data.UsersByHierarchy;
          this.refreshOptions();
        }
      });
    } else {
      this.actionDataService.getForwardUsers(-1, -1).subscribe(({ success, data }) => {
        if (success) {
          this.unfilteredForwardedUsers = data;
          this.usersByArea = data.ByArea ? data.ByArea : data.UsersByArea as any;
          this.usersByHierarchy = data.ByHierarchy ? data.ByHierarchy : data.UsersByHierarchy;
          this.refreshOptions();
        }
      });
    }

    this.subscription = leadsMultipleStepByStepService.listenToCancel().subscribe(data => {
      this.cancel = true;
    })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  clearSelectedDealer(): void {
    this.selectedPartner = undefined;
    this.selectedUserId = null;
    this.searchedText = '';
    this.searchTriggered = false;
    this.selectedValue = null;
    this.displayedSelectedText = null;
  }

  onSelect(value: string): void {
    this.selectedValue = value;
    this.hasError = false;
    const item = this.options[+value];

    if (item) {
      this.displayedSelectedText = item.shortName;
      if (item.byArea) {
        this.selectSearchStudioResultResultItem(item.value as IActionStudioSearchResult);
      } else {
        this.selectResultItem(item.value as IUser);
      }
    }
  }

  selectResultItem(result: IUser): void {
    this.resetIsAbsentAndPartnerData();
    if (result.Absence) {
      this.setIsAbsentUserData(result.Absence);
    }
    this.selectedUserId = result.Id;
    this.originalSearchedText = this.searchedText = result.FirstName + ' ' + result.LastName;
  }

  selectSearchStudioResultResultItem(result: IActionStudioSearchResult): void {
    this.resetIsAbsentAndPartnerData();

    if (!result.ContactManagers?.length) {
      this.selectedUserId = result.Id;
      this.selectedPartner = result;
    } else {
      this.selectedUserId = result.ContactManagers[0].Id;
      if (result.ContactManagers[0].Absence) {
        this.setIsAbsentUserData(result.ContactManagers[0].Absence);
      }
    }
    if (result.KKZ && result.ContactManagers?.length) {
      const manager = result.ContactManagers[0];
      this.originalSearchedText = this.searchedText = `${manager.FirstName} ${manager.LastName}`;
    } else {
      this.originalSearchedText = this.searchedText = result.Name;
    }
  }

  getStudioItemTooltip(item: IActionStudioSearchResult): string {
    let result = '';

    if (item?.KKZ && item?.ContactManagers?.length) {
      const manager = item.ContactManagers[0];
      result = `${manager.FirstName} ${manager.LastName}`;
    } else if (item?.Email) {
      result = item.Email;
    }

    return result;
  }

  get formIsValid(): boolean {
    if (this.selectedTab === this.tabTypes.dropdown) {
      if (this.isMany) {
        return this.leads.length > 0 && !!this.selectedUserId && !this.submitting && !this.inValidNote;
      }
      return !!this.leadId && !!this.selectedUserId && !this.submitting && !this.inValidNote;
    } else if (this.selectedTab === this.tabTypes.input) {
      this.selectedUserId = undefined;
      if (this.isMany) {
        return this.leads.length > 0 && !!this.checkboxChecked && !this.submitting && !this.inValidNote;
      }
      return !!this.externalEmail && this.checkboxChecked && !this.submitting && !this.inValidNote;
    }
    return false;
  }

  inValidMaxLength(): boolean {
    this.inValidNote = this.note.length > 2000;
    return this.inValidNote;
  }

  onOverlayClick(): void {
    this.selectActive = false;
    this.searchedText = null;
    this.studioUsersByHierarchy = [];
  }

  submitStepByStep(): void {
    if (this.cancel) {
      return;
    }
    this.clearClassAndCloseDialog(this.dialogRef, 'remove');
    if (this.selectedPartner) {
      this.actionDataService.sendKkz(this.leads[this.step], this.note, this.selectedPartner.KKZ, this.selectedPartner.Email)
        .subscribe(({ success, error }) => {
          if (this.leads.length - 1 === this.leads.lastIndexOf(this.leads[this.step])) {
            this.submitting = false;
          }

          this.step++;
          this.leadsMultipleStepByStepService.switchToNextStep();

          if (this.leads[this.step]) {
            this.submitStepByStep();
          }
        });
    } else {
      this.actionDataService.forwardRequest(this.leads[this.step], this.note, this.selectedUserId, this.forwardLeadTypeId, this.externalEmail)
        .subscribe(({ success, error }) => {
          if (this.leads.length - 1 === this.leads.lastIndexOf(this.leads[this.step])) {
            this.submitting = false;
          }

          this.step++;
          this.leadsMultipleStepByStepService.switchToNextStep();

          if (this.leads[this.step]) {
            this.submitStepByStep();
          }
        });
    }
  }

  submit(): void {
    this.hasError = false;
    if (this.formIsValid) {

      if (this.isMany) {
        let action: any = {
          Name: 'MULTIPLE_LEADS_STEP_BY_STEP',
          leadsLength: this.leads.length,
          actionName: 'LEAD_FORWARD'
        }
        ActionHelper.doAction({} as any, action, this.dialog, this.leads, true)?.afterClosed().subscribe();
        this.submitStepByStep();
        return;
      }

      if (this.selectedPartner) {
        this.submitting = true;

        this.actionDataService.sendKkz(this.leadId, this.note, this.selectedPartner.KKZ, this.selectedPartner.Email)
          .subscribe(({ success, error }) => {
            if (success) {
              this.clearClassAndCloseDialog(this.dialogRef, 'remove');
            } else {
              if (error === 'ERR_REL_LEAD_TENANT') {
                this.hasError = true;
              }
            }
            this.submitting = false;
          });
      } else {
        this.submitting = true;
        this.actionDataService.forwardRequest(this.leadId, this.note, this.selectedUserId, this.forwardLeadTypeId, this.externalEmail)
          .subscribe(({ success, error }) => {
            if (success) {
              this.clearClassAndCloseDialog(this.dialogRef, 'remove');
            } else {
              if (error === 'ERR_REL_LEAD_TENANT') {
                this.hasError = true;
              }
            }

            this.submitting = false;
          });
      }
    }
  }

  onSearch(text: string): void {
    text = text?.trim() ?? '';

    if (text === this.searchedText) {
      return;
    }

    this.searchedText = text;

    if (text) {
      this.actionDataService.studioSearch(text).subscribe(({data, success}) => {
        if (success && text === this.searchedText) {
          this.usersByArea = data;
          this.refreshOptions(false);
        }
      });
    } else {
      setTimeout(() => {
        if (text === this.searchedText) {
          const data = this.unfilteredForwardedUsers;
          this.usersByArea = data.ByArea ? data.ByArea : data.UsersByArea as any;
          this.usersByHierarchy = data.ByHierarchy ? data.ByHierarchy : data.UsersByHierarchy;
          this.refreshOptions();
        }
      }, 100)
    }
  }

  private resetIsAbsentAndPartnerData(): void {
    this.selectedPartner = null
    this.isAbsentUntil = undefined;
    this.userIsAbsent = false;
    this.isAbsentCoverUserName = undefined;
  }

  private setIsAbsentUserData(absent: IAbsence): void {
    this.isAbsentUntil = this.formatAbsenceDate(absent.Until);
    this.userIsAbsent = true;
    this.isAbsentCoverUserName = absent.Substitute.FirstName + ' ' + absent.Substitute.LastName;
  }

  private formatAbsenceDate(date: Date | string): string {
    if (typeof date === 'string') {
      const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return `${match[3]}.${match[2]}.${match[1]}`;
      }
    }

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) {
      return '';
    }

    const day = `0${parsedDate.getDate()}`.slice(-2);
    const month = `0${parsedDate.getMonth() + 1}`.slice(-2);

    return `${day}.${month}.${parsedDate.getFullYear()}`;
  }

  private refreshOptions(addSections = true): void {
    let index = 0;
    this.options = [];


    this.usersByHierarchy?.forEach(value => {
      let name = '', organization = '';

      if (value?.FirstName) {
        name += value?.FirstName + ' ';
      }

      if (value?.LastName) {
        name += value?.LastName;
      }

      if (value?.Organisation) {
        organization = value?.Organisation;
      }

      this.options.push({
        shortName: name,
        name:
          `
<div style="display: flex; gap: 10px; width: 100%; align-content: flex-start; white-space: normal; text-align: left; padding: 5px 0;">
<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 35%; min-width: 35%; max-width: 35%;">${TextHelper.escapeHtml(name)}</div>
<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 35%; min-width: 35%; max-width: 35%; opacity: 0.4">${TextHelper.escapeHtml(organization)}</div>
</div>`,
        id: index++,
        byArea: false,
        hasKKZ: false,
        value,
      })
    });

    this.usersByArea?.forEach(value => {
      let name = '', organization = '', kkz = '';

      if (value?.Name) {
        name = value?.Name;
      }

      const tooltip = this.getStudioItemTooltip(value);

      if (tooltip) {
        organization = tooltip;
      }

      if (value?.KKZ) {
        kkz = value?.KKZ;
      }

      this.options.push({
        shortName: name,
        name:
          `
<div style="display: flex; gap: 10px; overflow-wrap: break-word; width: 100%; align-content: flex-start; white-space: normal; text-align: left; padding: 5px 0;">
<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 35%; min-width: 35%; max-width: 35%;">${TextHelper.escapeHtml(name)}</div>
<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 35%; min-width: 35%; max-width: 35%; opacity: 0.4">${TextHelper.escapeHtml(organization)}</div>
<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 20%; min-width: 20%; max-width: 20%; opacity: 0.4">${TextHelper.escapeHtml(kkz)}</div>
</div>
`,
        id: index++,
        byArea: true,
        hasKKZ: !!kkz,
        value,
      })
    });

    /*
    this.sectionTextKeys.set(index, '%ACTION_LEAD_FORWARD_SEARCH_SUGGESTION_USER%');
    this.sectionTextKeys.set(index, '%ACTION_LEAD_FORWARD_SEARCH_SUGGESTION_PARTNER_STUDIOS%');
    */


    const firstFinstralOptionsCount = Math.min(3, this.usersByHierarchy?.length ?? 0);
    const firstStudiosOptionsCount = Math.min(6, this.usersByArea?.length ?? 0);

    const firstStudiosOptions = this.options.splice(this.usersByHierarchy?.length ?? 0, firstStudiosOptionsCount);
    const firstFinstralOptions = this.options.splice(0, firstFinstralOptionsCount);

    if (firstFinstralOptionsCount + firstStudiosOptionsCount) {
      if (firstFinstralOptionsCount) {
        this.sectionTextKeys.set(0, '<div style="font-size: 22px; margin-bottom: 10px;">%ACTION_LEAD_FORWARD_SEARCH_SUGGESTION_USER%</div><div>Finstral</div>');

        if (firstStudiosOptionsCount) {
          this.sectionTextKeys.set(firstFinstralOptionsCount, '%ACTION_LEAD_FORWARD_SEARCH_SUGGESTION_PARTNER_STUDIOS%');
        }
      } else {
        this.sectionTextKeys.set(0, '<div style="font-size: 22px; margin-bottom: 10px;">%ACTION_LEAD_FORWARD_SEARCH_SUGGESTION_USER%</div><div>%ACTION_LEAD_FORWARD_SEARCH_SUGGESTION_PARTNER_STUDIOS%</div>');
      }

      this.sectionTextKeys.set(firstFinstralOptionsCount + firstStudiosOptionsCount, '<hr style="opacity: 0.3">');
      this.options = [...firstFinstralOptions, ...firstStudiosOptions, ...this.options];
      this.options.forEach((opt, i) => opt.id = i);
    } else {
      this.options = [...this.options];
    }

    if (!addSections) {
      this.sectionTextKeys.clear();
      this.options = [...new Set(this.options)];
      this.options.sort((a, b) => (a.hasKKZ ? 1 : 0) - (b.hasKKZ ? 1 : 0));
      this.options.forEach((opt, i) => opt.id = i);
    }
  }
}
